import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins } from "@fortawesome/free-solid-svg-icons";
import "./RemoteAnalysisButton.css";

export default function TeamAnalysisButton({ apiBaseUrl, token, teamId, members, role, disabled, onAccepted }) {
  const key = `teamAnalysis:${localStorage.getItem("userID")}:${teamId}`;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [operation, setOperation] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(key));
      return stored?.teamId === teamId && /^[a-f0-9]{32}$/.test(stored.requestId) &&
        Array.isArray(stored.memberUserIds) && stored.memberUserIds.length > 0 && stored.memberUserIds.every(id => Number.isInteger(id) && id > 0) ? stored : null;
    } catch { return null; }
  });
  const [results, setResults] = useState([]);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const inFlight = useRef(false);
  const controller = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    const returnFocus = trigger.current;
    element.showModal();
    setLoadingCredits(true);
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 8000);
    let active = true;
    fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/credits/account`, { signal: abort.signal, headers: { Authorization: `Bearer ${token}` } })
      .then(async response => {
        const value = await response.json();
        if (!response.ok || !Number.isInteger(value.availableReports)) throw new Error();
        if (active) setCredits(value.availableReports);
      }).catch(() => { if (active) setCredits(null); })
      .finally(() => { if (active) setLoadingCredits(false); });
    return () => { active = false; clearTimeout(timeout); abort.abort(); element.close(); returnFocus?.focus(); };
  }, [open, apiBaseUrl, token]);

  async function start() {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError("");
    const next = operation || { requestId: crypto.randomUUID().replace(/-/g, ""), teamId, memberUserIds: members.map(member => member.userId) };
    controller.current = new AbortController();
    const timeout = setTimeout(() => controller.current?.abort(), 30000);
    try {
      // Save before sending; retries and reloads retain every member's request identity.
      sessionStorage.setItem(key, JSON.stringify(next));
      setOperation(next);
      const response = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/remote-analysis/team`, {
        method: "POST", signal: controller.current.signal,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(next),
      });
      const value = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(response.status === 401 ? "Sign in again to analyze your team."
        : response.status === 404 ? "Team analysis is not available yet. Try again later."
        : value.message || "The response could not be confirmed. Retry this team analysis.");
      if (!Array.isArray(value.results) || value.results.length !== next.memberUserIds.length ||
        new Set(value.results.map(result => result.targetUserId)).size !== next.memberUserIds.length ||
        value.results.some(result => !next.memberUserIds.includes(result.targetUserId) || (!result.request?.requestId && !result.error)))
        throw new Error("The response could not be confirmed. Retry this team analysis.");
      if (!mounted.current) return;
      setResults(value.results);
      onAccepted(value.results);
      if (value.results.every(result => result.request)) setOpen(false);
    } catch (reason) {
      if (mounted.current) setError(reason.name === "AbortError" ? "The response could not be confirmed. Retry this team analysis." : reason.message || "Team analysis could not be requested.");
    } finally {
      clearTimeout(timeout); inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const count = operation?.memberUserIds.length || members.length;
  const hasFailures = results.some(result => result.error);
  return <>
    <button ref={trigger} type="button" className="secondaryButton compactAction remoteAnalyzeButton team-analyze-button" disabled={disabled || busy || !members.length} aria-haspopup="dialog" onClick={() => setOpen(true)}>Analyze team</button>
    {open && <dialog ref={dialog} className="remoteAnalysisDialog team-analysis-dialog" aria-labelledby="team-analysis-title"
      onCancel={event => { event.preventDefault(); if (!busy) setOpen(false); }}>
      <header><h2 id="team-analysis-title">Analyze team</h2><button type="button" disabled={busy} aria-label="Close team analysis" onClick={() => setOpen(false)}>Close</button></header>
      <p>Request a five-minute analysis for all {count} team members, including you. Each completed analysis creates a separate PDF emailed to you and that member.</p>
      <p>{role === "Owner" || role === "Admin" ? "As Owner or Admin, you can start these analyses without member approval. Each device user can stop their analysis."
        : "Each member must approve the analysis on their device before it starts."}</p>
      <p>Uses each member's most recently connected device. Members without a connected SignalTuner app are listed for retry. Credits are checked when each analysis starts.</p>
      <p>Live report charts and status are visible to current members of your team.</p>
      {error && <p className="remoteAnalysisError" role="alert">{error}</p>}
      {busy && <p role="status">Requesting team analyses...</p>}
      {results.length > 0 && <ul className="team-analysis-results" aria-label="Team analysis results">{results.map(result => <li key={result.targetUserId}>
        <strong>{members.find(member => member.userId === result.targetUserId)?.name || "Team member"}</strong>
        <span>{result.error || "Requested. Follow progress in the member row below."}</span>
      </li>)}</ul>}
      <footer className="remoteAnalysisRequestActions">
        {results.length > 0 && <button type="button" disabled={busy} onClick={() => { sessionStorage.removeItem(key); setOperation(null); setResults([]); setError(""); }}>New team analysis</button>}
        {(!results.length || hasFailures || error) && <>
          <p><strong>Your account pays one credit per completed report up to {count} credits.</strong></p>
          <button type="button" className="remoteAnalysisRequestButton" disabled={busy || loadingCredits} onClick={() => void start()}>{results.length ? "Retry remaining members" : operation ? "Retry team analysis" : "Start team analysis"}</button>
        </>}
        <p aria-live="polite">Available credits: {loadingCredits ? "Loading..." : credits ?? "Unavailable"}{!loadingCredits && credits !== null && <FontAwesomeIcon icon={faCoins} className="remoteAnalysisCreditIcon" aria-hidden="true" />}</p>
      </footer>
    </dialog>}
  </>;
}
